import Ajv, { type ValidateFunction } from 'ajv/dist/2020'
import type { ErrorObject } from 'ajv'
import schema from '../../../types/mappings.schema.json' with { type: 'json' }
import { MappingsValidationError, type Destination } from '../../../types/mappings'

// The schema is compiled lazily, on first validation, NOT at module load.
//
// `ajv.compile()` builds the validator via `new Function(...)`. Some runtimes
// forbid code generation from strings — notably Cloudflare Workers (the SSR
// runtime for Nuxt apps on CF Pages), which throws "Code generation from
// strings disallowed for this context". Compiling at module top-level made the
// mere *import* of montilla-core crash every SSR render, even though the server
// never calls `validateDestination` (the Nuxt plugin no-ops on the server).
//
// Deferring compilation to first call keeps the import side-effect-free: the
// `new Function` only runs where validation actually happens (the browser).
let compiled: ValidateFunction<Destination> | undefined
function getValidate(): ValidateFunction<Destination> {
  if (!compiled) {
    const ajv = new Ajv({ allErrors: true, strict: false })
    compiled = ajv.compile<Destination>(schema)
  }
  return compiled
}

/**
 * Validates an arbitrary JSON value against the per-`Destination` v1.0 schema
 * (a single entry inside the `MappingsResponse.destinations[]` envelope).
 * Throws `MappingsValidationError` with the ajv errors on failure.
 *
 * Used both for bundled JSON files (build-time confidence) and remote-fetched
 * responses (runtime safety, per envelope item).
 */
export function validateDestination(json: unknown): Destination {
  const validate = getValidate()
  if (validate(json)) {
    return json as Destination
  }
  const errors: ErrorObject[] = validate.errors ?? []
  const detail = errors
    .map(e => `  - ${e.instancePath || '(root)'} ${e.message ?? 'invalid'}`)
    .join('\n')
  throw new MappingsValidationError(
    `[montilla] Destination JSON failed schema validation:\n${detail}`,
    errors,
  )
}

/** @deprecated Use `validateDestination`. Kept temporarily for callers in transit. */
export const validateMappings = validateDestination
